import {
  FiltersRequestBody,
  GetTrimLevelsApiResponse,
  RelativeFilterConfigBody,
  StatisticsV2RequestBody,
} from '../../../types/api/api';
import {FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';
import type {IsKnownPresetFeature} from '../../Filter/components/SavedFilters/utils/normalizePresetFeatures';
import {
  buildPresetApplyContext,
  PRESET_CONTROLLED_FORM_FIELDS,
  resolvePresetFormFieldValue,
} from '../../Filter/components/SavedFilters/utils/presetFieldResolvers';
import {formValuesToRequestValues} from '../../Filter/utils/formValuesToRequestValues';

/**
 * Matches StatisticsPage page size so the collapsed-panel query and the chart query
 * share one RTK Query cache entry when the request bodies are equal.
 */
export const METRICS_PAGE_SIZE = 1000;

/**
 * Normalises a filter request body into the panel-metrics shape: all market categories
 * (in advertisement / sold 30 / sold 90) are force-included so counts and averages can
 * be computed even when the user hides a category in the chart view toggles.
 */
export function toMetricsRequestBody(
  filter: FiltersRequestBody,
  queryPrice: number | null
): StatisticsV2RequestBody {
  return {
    ...filter,
    hideStockCars: undefined,
    hideSoldIn30: undefined,
    hideSoldIn90: undefined,
    query_price: queryPrice,
    offset: 0,
    size: METRICS_PAGE_SIZE,
  };
}

/**
 * Resolves a saved-filter preset config into complete filter form values without touching
 * the live form. Mirrors what applyPresetToForm would produce: base values with every
 * preset-controlled field overwritten by its resolver.
 */
export function resolvePresetFormValues(
  config: RelativeFilterConfigBody,
  priceReport: PriceReportType,
  baseFormValues: FilterFormData,
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null,
  isKnownFeature?: IsKnownPresetFeature
): FilterFormData {
  const ctx = buildPresetApplyContext(
    config,
    priceReport,
    trimLevelsCatalog,
    isKnownFeature ?? (() => true)
  );

  const resolved: FilterFormData = {...baseFormValues};
  for (const field of PRESET_CONTROLLED_FORM_FIELDS) {
    // Same field-by-field write applyPresetToForm performs via formApi.setValue
    setFormField(resolved, field, resolvePresetFormFieldValue(field, ctx));
  }
  return resolved;
}

/** Keeps the key–value correlation TypeScript loses when indexing with a union key. */
function setFormField<K extends keyof FilterFormData>(
  values: FilterFormData,
  field: K,
  value: FilterFormData[K]
): void {
  values[field] = value;
}

/**
 * Builds the statistics_v2 request body answering: “what market data would this preset
 * show if selected?” — preset config resolved over the current form values, mapped to
 * request values, normalised for metrics.
 */
export function buildPresetMetricsRequestBody(
  config: RelativeFilterConfigBody,
  priceReport: PriceReportType,
  baseFormValues: FilterFormData,
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null,
  isKnownFeature?: IsKnownPresetFeature
): StatisticsV2RequestBody {
  const formValues = resolvePresetFormValues(
    config,
    priceReport,
    baseFormValues,
    trimLevelsCatalog,
    isKnownFeature
  );
  return toMetricsRequestBody(
    formValuesToRequestValues(formValues),
    priceReport.sellingPrice.recommended.value ?? null
  );
}
